import { useEffect, useState, memo, useRef } from 'react'
import { useDispatch } from 'react-redux'
import FileUploader from 'react-firebase-file-uploader'

import { Box, makeStyles, CircularProgress } from '@material-ui/core'

import { getUploadRef, uuid } from 'modules'
import { openUploadProgressPopup } from 'ducks/actions'

import { useUploader } from 'hooks'

const useStyles = makeStyles({
    menuItemLabel: {
        margin: 0,
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer'
    }
})

const ALLOWED_FORMATS = '.mp4, .mov, .webm'

export const VideoUploader = memo(({ labelComponent, onUploadCallback }) => {
    const classes = useStyles()
    const dispatch = useDispatch()

    const archiveRef = useRef(null)
    const {
        uid,
        filesToStore,
        fileUploader,
        postProcessing,
        handleUploadSuccess,
        handleUploadError,
        customOnChangeHandler,
        startUploadManually
    } = useUploader({ onUploadCallback })

    const [progress, setProgress] = useState({})

    const showCircularProgress =
        Object.values(progress).some(it => it.progress !== 100) || postProcessing

    useEffect(() => {
        const filesObject = Object.entries(progress).reduce(
            (a, [id, item]) => ({
                ...a,
                [id]: {
                    id: id,
                    name: item.name,
                    progress: item.progress,
                    source: 'upload',
                    type: 'video'
                }
            }),
            {}
        )

        dispatch(openUploadProgressPopup(filesObject))
    }, [dispatch, progress])

    useEffect(() => {
        if (showCircularProgress) return
        setProgress({})
    }, [showCircularProgress])

    useEffect(() => {
        if (!filesToStore) return
        startUploadManually()
    }, [filesToStore, startUploadManually])

    return (
        <label className={classes.menuItemLabel}>
            {labelComponent}
            {showCircularProgress && (
                <Box ml={1} display="flex">
                    <CircularProgress size={20} />
                </Box>
            )}

            <FileUploader
                hidden
                multiple
                accept={ALLOWED_FORMATS}
                name="playbook"
                // don't touch or modify the line bellow
                filename={e => {
                    const id = uuid()
                    archiveRef.current = {
                        ...archiveRef.current,
                        [id]: e.name
                    }

                    return id
                }}
                storageRef={getUploadRef(uid)}
                onUploadError={handleUploadError}
                onUploadSuccess={(filename, data) => {
                    const playbookId = filename.replace(/\..*/, '')
                    getUploadRef(uid)
                        .child(filename)
                        .getDownloadURL()
                        .then(url => {
                            handleUploadSuccess(
                                'upload_from_local_drive',
                                playbookId,
                                archiveRef.current[playbookId],
                                data.blob_.data_,
                                url
                            )
                        })
                }}
                onProgress={(progress, data) => {
                    const playbookId = data.location_.path
                        .replace(/(.*\/)*/, '')
                        .replace(/\..*/, '')

                    setProgress(prev => ({
                        ...prev,
                        [playbookId]: {
                            progress,
                            name: data.blob_.data_.name
                        }
                    }))
                }}
                onChange={customOnChangeHandler} // ⇐ Call your handler
                ref={instance => {
                    fileUploader.current = instance
                }} // ⇐ reference the component
            />
        </label>
    )
})
